import React from 'react';
import { Layout, Button, Dropdown } from 'antd';
import type { MenuProps } from 'antd';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  TrophyOutlined,
  TeamOutlined,
  SettingOutlined,
  LoginOutlined,
  LogoutOutlined,
  UserOutlined,
  MailOutlined,
  DollarOutlined,
} from '@ant-design/icons';
import LanguageSwitcher from './LanguageSwitcher';
import NotificationBell from './NotificationBell';

const { Header } = Layout;

const Navbar: React.FC = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const getMenuItems = () => {
    const items = [
      {
        key: 'tournaments',
        path: '/tournaments',
        icon: <TrophyOutlined />,
        label: 'Турниры',
      },
      {
        key: 'pricing',
        path: '/subscribe',
        icon: <DollarOutlined />,
        label: 'Цены',
      },
      {
        key: 'contact',
        path: '/contact',
        icon: <MailOutlined />,
        label: 'Контакты',
      },
    ];

    if (user?.role === 'manager') {
      items.push({
        key: 'club',
        path: '/club',
        icon: <TeamOutlined />,
        label: 'Управление клубом',
      });
    }

    if (user?.role === 'admin') {
      items.push({
        key: 'admin',
        path: '/admin',
        icon: <SettingOutlined />,
        label: 'Админ панель',
      });
    }

    return items;
  };

  const menuItems = getMenuItems();
  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Профиль',
      onClick: () => navigate('/profile'),
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: 'Настройки',
      onClick: () => navigate('/settings'),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Выйти',
      onClick: () => {
        // logout() сам делает редирект на /login
        logout();
      },
    },
  ];

  const authContent = isAuthenticated ? (
    <>
      <NotificationBell />
      <Dropdown 
        menu={{ items: userMenuItems }} 
        placement="bottomRight"
        trigger={['click']}
      >
        <Button 
          type="text" 
          icon={<UserOutlined />}
          style={{ 
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            fontSize: '16px',
          }}
        >
          {user?.email?.split('@')[0]}
        </Button>
      </Dropdown>
    </>
  ) : (
    <Button 
      icon={<LoginOutlined />} 
      onClick={() => navigate('/login')}
    >
      Войти
    </Button>
  );

  return (
    <Header style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 1000,
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0 24px',
    }}>
      <div style={{ 
        display: 'flex',
        alignItems: 'center',
        gap: '24px',
        flex: 1,
      }}>
        <Link to="/" style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '16px',
          textDecoration: 'none',
          color: 'white',
          whiteSpace: 'nowrap',
        }}>
          <img 
            src="/logo.png" 
            alt="karate-online" 
            style={{ 
              height: '48px', 
              width: 'auto',
              objectFit: 'contain',
            }}
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = '/logo.svg';
            }}
          />
        </Link>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          flexWrap: 'nowrap',
        }}>
          {menuItems.map((item) => (
            <Link
              key={item.key}
              to={item.path}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '0 16px',
                height: '64px',
                lineHeight: '64px',
                color: isActive(item.path) ? '#1890ff' : 'rgba(255, 255, 255, 0.85)',
                textDecoration: 'none',
                transition: 'color 0.3s',
                borderBottom: isActive(item.path) ? '2px solid #1890ff' : '2px solid transparent',
                whiteSpace: 'nowrap',
              }}
              onMouseEnter={(e) => {
                if (!isActive(item.path)) {
                  e.currentTarget.style.color = 'white';
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive(item.path)) {
                  e.currentTarget.style.color = 'rgba(255, 255, 255, 0.85)';
                }
              }}
            >
              {item.icon}
              <span>{item.label}</span>
            </Link>
          ))}
        </div>
      </div>
      <div style={{ 
        position: 'absolute',
        right: '24px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
      }}>
        <LanguageSwitcher />
        {authContent}
      </div>
    </Header>
  );
};

export default Navbar;